import { FileText, Upload, ShieldCheck, Download } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { AsyncBoundary } from '@kollez/design-system';
import { apiGet } from '@/lib/api';
import { iconForFile } from '@/lib/fileIcon';

/**
 * Standard documents a placement office expects. Existing uploads for this student are
 * matched to the requested kinds; anything unmatched shows "Missing".
 */
const REQUIRED_KINDS: Array<{ label: string; kind: string; helper?: string }> = [
  { label: 'Aadhaar', kind: 'aadhaar' },
  { label: 'Class 10 marksheet', kind: 'class10_marksheet' },
  { label: 'Class 12 marksheet', kind: 'class12_marksheet' },
  { label: 'Semester grade sheets', kind: 'semester_marksheet', helper: 'One per completed semester' },
  { label: 'Resume', kind: 'resume' },
  { label: 'Passport-size photo', kind: 'passport_photo' },
  { label: 'Category certificate', kind: 'category_certificate', helper: 'Optional if not applicable' },
];

interface Document { _id: string; kind: string; title: string; url: string; verifiedBy?: string; verifiedAt?: string; }

function statusFor(uploaded: Document[]): 'Verified' | 'On file' | 'Missing' {
  if (uploaded.length === 0) return 'Missing';
  if (uploaded.some((u) => u.verifiedBy)) return 'Verified';
  return 'On file';
}

/**
 * /documents — student document vault.
 *
 * Deep-audit fixes (Page 15/N):
 *   - 'Add document' button fired a toast saying 'Upload flow lands
 *     next…' — a fake action masquerading as a real one. Now disabled
 *     with an honest 'soon' label and a title tooltip.
 *   - Extra documents the student uploads outside the required set
 *     (internship letters, project certificates, extras) were grouped
 *     but never displayed. Added an 'Other documents' section that
 *     surfaces every non-required kind so students can see their
 *     whole vault.
 */
export function StudentDocumentsPage() {
  const query = useQuery({
    queryKey: ['student', 'me', 'documents'],
    queryFn: () => apiGet<{ rows: Document[] }>('/students/me/documents'),
  });

  const rows = query.data?.rows ?? [];
  const grouped = new Map<string, Document[]>();
  for (const d of rows) {
    const list = grouped.get(d.kind) ?? [];
    list.push(d);
    grouped.set(d.kind, list);
  }

  const missing = REQUIRED_KINDS.filter((k) => (grouped.get(k.kind)?.length ?? 0) === 0 && k.kind !== 'category_certificate').length;

  const requiredKindSet = new Set(REQUIRED_KINDS.map((k) => k.kind));
  const extras = rows.filter((r) => !requiredKindSet.has(r.kind));

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">My profile</p>
          <h1 className="h-page">Documents</h1>
          <p className="subhead">One place for the papers companies and your placement office ask for.</p>
        </div>
        <div className="ph-actions">
          <button
            className="btn btn-primary"
            disabled
            title="Upload UI ships in the next release. Ask your placement office to upload for you today."
          >
            <Upload size={14} />Add document · soon
          </button>
        </div>
      </div>

      {missing > 0 && (
        <div className="notice notice-warning">
          <ShieldCheck size={14} />
          <div><b>{missing} required document{missing === 1 ? '' : 's'} missing.</b> Companies verify these before releasing offers.</div>
        </div>
      )}

      <AsyncBoundary kind="table" loading={query.isLoading} error={query.error}>
        <div className="card">
          <div className="card-head"><h2 className="h-section">Required</h2></div>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead><tr><th>Document</th><th>Status</th><th className="hide-mobile">Uploaded</th><th className="r"></th></tr></thead>
              <tbody>
                {REQUIRED_KINDS.map((k) => {
                  const uploads = grouped.get(k.kind) ?? [];
                  const status = statusFor(uploads);
                  const latest = uploads[0];
                  const Glyph = latest ? iconForFile(latest.url) : FileText;
                  return (
                    <tr key={k.kind}>
                      <td>
                        <div className="flex items-center gap-3">
                          <Glyph size={16} className="text-tertiary" />
                          <div>
                            <div className="cell-title">{k.label}</div>
                            {k.helper && <div className="cell-sub">{k.helper}</div>}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${status === 'Verified' ? 'badge-success' : status === 'Missing' ? 'badge-warning' : ''}`}>{status}</span>
                      </td>
                      <td className="hide-mobile cell-sub">
                        {latest?.verifiedAt
                          ? new Date(latest.verifiedAt).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })
                          : latest ? 'Awaiting' : '—'}
                      </td>
                      <td className="r">
                        {latest && (
                          <a className="btn btn-ghost btn-sm" href={latest.url} target="_blank" rel="noopener noreferrer">
                            <Download size={12} />Open
                          </a>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {extras.length > 0 && (
          <div className="card">
            <div className="card-head">
              <h2 className="h-section">Other documents</h2>
              <span className="helper">{extras.length} file{extras.length === 1 ? '' : 's'}</span>
            </div>
            <div className="tbl-wrap">
              <table className="tbl">
                <thead><tr><th>Title</th><th>Kind</th><th className="hide-mobile">Verified</th><th className="r"></th></tr></thead>
                <tbody>
                  {extras.map((d) => {
                    const Glyph = iconForFile(d.url);
                    return (
                      <tr key={d._id}>
                        <td>
                          <div className="flex items-center gap-3">
                            <Glyph size={16} className="text-tertiary" />
                            <div className="cell-title">{d.title}</div>
                          </div>
                        </td>
                        <td className="cell-sub">{d.kind}</td>
                        <td className="hide-mobile cell-sub">
                          {d.verifiedAt
                            ? new Date(d.verifiedAt).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })
                            : '—'}
                        </td>
                        <td className="r">
                          <a className="btn btn-ghost btn-sm" href={d.url} target="_blank" rel="noopener noreferrer">
                            <Download size={12} />Open
                          </a>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </AsyncBoundary>
    </div>
  );
}
